import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useUtilities from "../../data/useUtilities";
import CustomDatePicker from "../../components/awaitingInventory/CustomDatePicker";
import { DateRangeType as PickerDateRangeType } from "react-tailwindcss-datepicker";
import { DateTime } from "luxon";

import PagedPaymentTable from "../../components/payments/PagedPaymentTable";
import useCustomerDetails from "../../data/useCustomerDetails";

export default function Payments() {
  const { setInfo } = usePageNameContext();
  const { getTellerooAccount } = useUtilities();
  // const { features } = useCustomerDetails();

  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get("search") || "";

  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [includeSent, setIncludeSent] = useState(false);
  const [hideFailed, setHideFailed] = useState(false);

  const [dates, setDates] = useState<PickerDateRangeType>({
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    setInfo({
      name: "Payments",
      desc: "Payments that have been raised will be shown here",
    });
  }, []);

  // keep local searchTerm in sync if URL changes elsewhere
  useEffect(() => {
    const urlSearch = searchParams.get("search") || "";
    if (urlSearch !== searchTerm) setSearchTerm(urlSearch);
  }, [searchParams]);

  const handleDatesChange = (newDates: PickerDateRangeType) => {
    setDates(newDates);
  };

  const handleIncludeSent = (checked: boolean) => {
    if (checked) {
      setDates({
        startDate: DateTime.now().minus({ months: 1 }).toJSDate(),
        endDate: DateTime.now().toJSDate(),
      });
    } else {
      setDates({ startDate: null, endDate: null });
    }
    setIncludeSent(checked);
  };

  const onSearchChange = (val: string) => {
    setSearchTerm(val);

    const sp = new URLSearchParams(searchParams);
    if (val.trim()) sp.set("search", val);
    else sp.delete("search");
    setSearchParams(sp, { replace: true });
  };

  // This becomes PaymentFilterDto (inside TableSorting.searchBody)
  const additionalBody = useMemo(() => {
    return {
      includeSent,
      hideFailed,
      createdDate:
        includeSent && (dates.startDate || dates.endDate)
          ? {
              fieldName: "Created",
              start: dates.startDate,
              end: dates.endDate,
            }
          : null,
    };
  }, [includeSent, hideFailed, dates]);

  if (getTellerooAccount.isLoading) {
    return (
      <div className="mt-8 min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <>
      <div className="mt-4 flex gap-x-5">
        <div className="rounded bg-green-100 text-green-600 p-2 px-4">
          <h1 className="text-green-600 m-0 text-xl font-semibold">
            {new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
              getTellerooAccount?.data?.data?.balance ? getTellerooAccount.data.data.balance / 100 : 0
            )}
          </h1>
          <p className="m-0">Telleroo Balance</p>
        </div>

        <div className="rounded bg-red-100 text-red-600 p-2 px-4">
          <h1 className="text-red-600 m-0 text-xl font-semibold">
            {new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
              getTellerooAccount?.data?.data?.awaitingApprovalBalance
                ? getTellerooAccount.data.data.awaitingApprovalBalance / 100
                : 0
            )}
          </h1>
          <p className="m-0">Awaiting Approval</p>
        </div>
      </div>

      <div className="mt-4 flex justify-between">
        <div className="flex gap-x-4">
          <div className="relative flex items-stretch flex-grow focus-within:z-10">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10 sm:text-sm lg:min-w-[250px] sm:min-w-[unset] max-w-full lg:max-w-[calc(100% - 3rem)]"
              placeholder="Name, Price, VRM, Postcode"
            />
          </div>

          {includeSent && <CustomDatePicker dates={dates} handleDatesChange={handleDatesChange} />}
        </div>

        <div className="flex gap-x-4 items-center">
          <div>
            <input
              id="showSent"
              onChange={(e) => handleIncludeSent(e.target.checked)}
              checked={includeSent}
              type="checkbox"
              className="mr-2 h-5 w-5"
            />
            <label htmlFor="showSent">Include Sent</label>
          </div>

          <div>
            <input
              id="hideFailed"
              onChange={(e) => setHideFailed(e.target.checked)}
              checked={hideFailed}
              type="checkbox"
              className="mr-2 h-5 w-5"
            />
            <label htmlFor="hideFailed">Hide failed</label>
          </div>
        </div>
      </div>

      <PagedPaymentTable additionalBody={additionalBody} />
    </>
  );
}